// Decision Audit Trail Types
export type DecisionType = 
  | 'security_response'
  | 'resource_allocation' 
  | 'beneficiary_selection'
  | 'program_modification'
  | 'partner_management'
  | 'other';

export type RiskLevel = 'low' | 'medium' | 'high';

export type DecisionStatus = 'draft' | 'submitted' | 'synced' | 'archived';

export type EvidenceType = 'photo' | 'document' | 'audio' | 'screenshot';

export type UploadStatus = 'pending' | 'uploading' | 'complete' | 'failed';

export type SyncStatus = 'synced' | 'pending' | 'conflict' | 'failed';

export interface Location {
  lat: number;
  lng: number;
  address?: string;
  accuracy?: number;
}

export interface Evidence {
  id: string;
  type: EvidenceType;
  filename: string;
  size: number;
  mimeType: string;
  url?: string; // When synced to server
  localPath?: string; // When stored locally
  thumbnail?: string; // Base64 thumbnail for images
  uploadStatus: UploadStatus;
  uploadedAt?: Date;
  metadata?: {
    duration?: number; // For audio files
    dimensions?: { width: number; height: number }; // For images
    pages?: number; // For documents
  };
}

export interface DecisionTemplate {
  id: string;
  name: string;
  type: DecisionType;
  description: string;
  fields: {
    title: string;
    rationale: string;
    informationSources: string[];
    suggestedStakeholders: string[];
  };
}

export interface Decision {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  createdByName: string;
  status: DecisionStatus;
  syncStatus: SyncStatus;
  
  // Core decision data
  title: string;
  type: DecisionType;
  decisionMade: string;
  rationale: string;
  riskLevel: RiskLevel;
  
  // Context and process
  informationSources: string[];
  stakeholders: string[];
  alternatives?: string;
  location?: Location;
  
  // Evidence and attachments
  evidence: Evidence[];
  
  // Audit and security
  signature: string; // SHA-256 hash for tamper detection
  deviceId: string;
  originalHash?: string; // For detecting modifications
  editHistory: DecisionEdit[];
  
  // Workflow
  templateId?: string;
  tags: string[];
  priority: 'low' | 'normal' | 'high' | 'urgent';
  
  // Review and approval
  reviewedBy?: string;
  reviewedAt?: Date;
  reviewNotes?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  
  // Offline support
  lastSyncAttempt?: Date;
  conflictData?: Partial<Decision>;
}

export interface DecisionEdit {
  id: string;
  editedAt: Date;
  editedBy: string;
  editedByName: string;
  field: string;
  oldValue: any;
  newValue: any;
  reason?: string;
}

export interface DecisionFilter {
  dateFrom?: Date;
  dateTo?: Date;
  types?: DecisionType[];
  riskLevels?: RiskLevel[];
  statuses?: DecisionStatus[];
  createdBy?: string[];
  tags?: string[];
  searchTerm?: string;
  syncStatus?: SyncStatus[];
}

export interface DecisionListResponse {
  decisions: Decision[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ExportOptions {
  format: 'pdf' | 'excel' | 'zip';
  template: 'incident_report' | 'monthly_summary' | 'audit_package' | 'custom';
  includeEvidence: boolean;
  dateRange?: {
    from: Date;
    to: Date;
  };
  filters?: DecisionFilter;
}

export interface SyncConflict {
  decisionId: string;
  field: string;
  localValue: any;
  serverValue: any;
  lastModified: {
    local: Date;
    server: Date;
  };
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  conflicts: SyncConflict[];
  errors: string[];
}

// Form validation types
export interface ValidationError {
  field: string;
  message: string;
}

export interface FormState {
  isValid: boolean;
  errors: ValidationError[];
  isSubmitting: boolean;
  isDirty: boolean;
}

// Notification types
export interface NotificationOptions {
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Analytics and metrics
export interface DecisionMetrics {
  totalDecisions: number;
  decisionsByType: Record<DecisionType, number>;
  decisionsByRisk: Record<RiskLevel, number>;
  averageDecisionTime: number;
  pendingSync: number;
  recentActivity: {
    date: Date;
    count: number;
  }[];
}

// User preferences
export interface UserPreferences {
  defaultRiskLevel: RiskLevel;
  autoSaveInterval: number; // minutes
  enableOfflineMode: boolean;
  preferredTemplates: string[];
  quickAccessStakeholders: string[];
  notificationSettings: {
    syncComplete: boolean;
    syncFailed: boolean;
    reminderInterval: number; // hours
  };
}

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    hasMore: boolean;
  };
}

// Constants
export const DECISION_TYPES: Record<DecisionType, string> = {
  security_response: 'Security Response',
  resource_allocation: 'Resource Allocation',
  beneficiary_selection: 'Beneficiary Selection', 
  program_modification: 'Program Modification',
  partner_management: 'Partner Management',
  other: 'Other'
};

export const RISK_LEVELS: Record<RiskLevel, string> = {
  low: 'Low Risk',
  medium: 'Medium Risk', 
  high: 'High Risk'
};

export const INFORMATION_SOURCES = [
  'Field Reports',
  'Satellite Imagery',
  'Government Sources',
  'UN Reports',
  'Partner Organizations',
  'Local Authorities',
  'Community Leaders',
  'Media Reports',
  'Social Media',
  'Direct Observation',
  'Beneficiary Feedback',
  'Technical Assessments',
  'Security Briefings',
  'Weather Reports',
  'Other'
];

export const PRIORITY_LEVELS = {
  low: 'Low',
  normal: 'Normal',
  high: 'High', 
  urgent: 'Urgent'
};

// File size limits
export const FILE_SIZE_LIMITS = {
  maxFileSize: 5 * 1024 * 1024, // 5MB per file
  maxTotalSize: 50 * 1024 * 1024, // 50MB total per decision
  maxAudioDuration: 120, // 2 minutes in seconds
};

// Supported file types
export const SUPPORTED_FILE_TYPES = {
  images: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  documents: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'],
  audio: ['.mp3', '.wav', '.m4a', '.ogg'],
  video: ['.mp4', '.webm', '.mov'] // Optional for future
};

export const MIME_TYPES = {
  'image/jpeg': '.jpg',
  'image/png': '.png', 
  'image/gif': '.gif',
  'image/webp': '.webp',
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'text/plain': '.txt',
  'audio/mpeg': '.mp3',
  'audio/wav': '.wav',
  'audio/mp4': '.m4a',
  'audio/ogg': '.ogg'
};